import React, { useEffect, useRef, useState } from 'react';
import {
  Trophy,
  Timer,
  BarChart3,
  CheckCircle2,
  Layers,
  ArrowUpRight,
  Sparkles,
} from 'lucide-react';

// ---------- Animated counter ----------
const useCounter = (to, duration = 1800) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min((t - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(to * eased);
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);

  return { ref, n };
};

// ---------- Mini visualizations ----------

// Rising bar chart — for "Tenders Won"
const MiniBars = ({ color = '#F59E0B' }) => {
  const bars = [22, 30, 28, 45, 58, 70, 95];
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setAnimated(true));
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="flex items-end gap-[2px] h-8 w-16">
      {bars.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-[1px]"
          style={{
            height: animated ? `${h}%` : '6%',
            background: `linear-gradient(180deg, ${color} 0%, ${color}66 100%)`,
            transition: `height 700ms cubic-bezier(0.22,1,0.36,1) ${i * 70}ms`,
            boxShadow: `0 0 6px ${color}55`,
          }}
        />
      ))}
    </div>
  );
};

// Circular progress ring — for "Success Rate"
const ProgressRing = ({ value = 94, size = 40, stroke = 4, color = '#34D399' }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min((t - start) / 1800, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setProgress(value * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value]);

  const offset = circumference - (progress / 100) * circumference;
  return (
    <svg ref={ref} width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size/2} cy={size/2} r={radius} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size/2} cy={size/2} r={radius}
        stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 60ms linear', filter: `drop-shadow(0 0 5px ${color}55)` }}
      />
    </svg>
  );
};

// Countdown clock arc — for "48-Hour Turnaround"
const ClockArc = ({ size = 40, color = '#A78BFA' }) => {
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          // looped progress for clock feel
          let raf;
          const start = performance.now();
          const tick = (t) => {
            const elapsed = (t - start) % 2400;
            setProgress((elapsed / 2400) * 100);
            raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
          return () => cancelAnimationFrame(raf);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const offset = circumference - (progress / 100) * circumference;
  return (
    <div ref={ref} className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle cx={size/2} cy={size/2} r={radius} stroke="rgba(255,255,255,0.08)" strokeWidth={3} fill="none" />
        <circle
          cx={size/2} cy={size/2} r={radius}
          stroke={color} strokeWidth={3} fill="none" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 40ms linear', filter: `drop-shadow(0 0 5px ${color}66)` }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Timer className="w-3.5 h-3.5" style={{ color }} />
      </div>
    </div>
  );
};

// Filling stripe — for "100% On-Time Submission"
const OnTimeBar = ({ color = '#60A5FA' }) => {
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const duration = 1600;
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min((t - start) / duration, 1);
            setProgress(1 - Math.pow(1 - p, 3));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Render 5 stacked stripes (like submission slips), filling left → right
  return (
    <div ref={ref} className="flex flex-col gap-[3px] w-16">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(progress * 100 + (i % 2 === 0 ? -2 : 0), 100)}%`,
              background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)`,
              transition: 'width 80ms linear',
              boxShadow: `0 0 6px ${color}55`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Segmented pie wedge — for "Sectors Served"
const PieWedges = ({ color = '#F472B6' }) => {
  // 6 wedges, animated radially
  const wedges = 6;
  const radius = 14;
  const cx = 16;
  const cy = 16;
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const duration = 1400;
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min((t - start) / duration, 1);
            setProgress(1 - Math.pow(1 - p, 3));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const arcs = [];
  for (let i = 0; i < wedges; i++) {
    const startA = (i / wedges) * Math.PI * 2 - Math.PI / 2;
    const endA = ((i + 0.85) / wedges) * Math.PI * 2 - Math.PI / 2;
    const r = radius * (0.5 + (i / wedges) * 0.5) * progress;
    const x1 = cx + Math.cos(startA) * r;
    const y1 = cy + Math.sin(startA) * r;
    const x2 = cx + Math.cos(endA) * r;
    const y2 = cy + Math.sin(endA) * r;
    arcs.push(
      <path
        key={i}
        d={`M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 0 1 ${x2},${y2} Z`}
        fill={color}
        fillOpacity={0.25 + (i / wedges) * 0.55}
        style={{ filter: `drop-shadow(0 0 3px ${color}44)` }}
      />
    );
  }
  return (
    <svg ref={ref} viewBox="0 0 32 32" width="36" height="36">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      {arcs}
    </svg>
  );
};

// ---------- Number renderers ----------
const Tenders = () => {
  const { ref, n } = useCounter(250);
  return <span ref={ref}>{Math.round(n)}+</span>;
};
const Hours = () => {
  const { ref, n } = useCounter(48);
  return <span ref={ref}>{Math.round(n)}<span className="text-base ml-1">Hr</span></span>;
};
const Success = () => {
  const { ref, n } = useCounter(94);
  return <span ref={ref}>{Math.round(n)}%</span>;
};
const Portals = () => {
  const { ref, n } = useCounter(100);
  return <span ref={ref}>{Math.round(n)}%</span>;
};
const Sectors = () => {
  const { ref, n } = useCounter(20);
  return <span ref={ref}>{Math.round(n)}+</span>;
};

// ---------- Tile ----------
const Tile = ({ icon: Icon, accent, viz, value, label, sub, dividerStart = false }) => {
  const accentMap = {
    amber: { ring: 'bg-amber-500/15', icon: 'text-amber-400', text: 'text-amber-400' },
    violet: { ring: 'bg-violet-500/15', icon: 'text-violet-400', text: 'text-violet-400' },
    emerald: { ring: 'bg-emerald-500/15', icon: 'text-emerald-400', text: 'text-emerald-400' },
    blue: { ring: 'bg-blue-500/15', icon: 'text-blue-400', text: 'text-blue-400' },
    pink: { ring: 'bg-pink-500/15', icon: 'text-pink-400', text: 'text-pink-400' },
  };
  const a = accentMap[accent] || accentMap.amber;

  return (
    <div
      className={`group relative p-4 sm:p-5 overflow-hidden ${dividerStart ? 'border-l border-white/5' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3 min-h-[40px]">
        <span className={`w-8 h-8 rounded-lg ${a.ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${a.icon}`} />
        </span>
        <div className="flex items-center">{viz}</div>
      </div>
      <div className={`text-2xl sm:text-[28px] font-extrabold tracking-tight ${a.text} leading-none`}>
        {value}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold leading-tight">
        {label}
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500 leading-tight">{sub}</div>
    </div>
  );
};

const HeroStatsRibbon = () => {
  return (
    <div className="relative w-full max-w-5xl">
      <div className="relative rounded-2xl p-[1px] overflow-hidden">
        <div
          className="absolute inset-0 rounded-2xl opacity-90"
          style={{
            background:
              'conic-gradient(from 0deg at 50% 50%, rgba(245,158,11,0.0) 0deg, rgba(245,158,11,0.6) 50deg, rgba(167,139,250,0.0) 110deg, rgba(96,165,250,0.5) 180deg, rgba(52,211,153,0.0) 250deg, rgba(244,114,182,0.4) 310deg, rgba(245,158,11,0.0) 360deg)',
            animation: 'ribbon-rotate 9s linear infinite',
          }}
        />
        <style>{`
          @keyframes ribbon-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes shimmer { 0% { transform: translateX(-150%); } 100% { transform: translateX(150%); } }
        `}</style>

        <div className="rtp-box relative rounded-2xl bg-[#0a0f1e]/85 backdrop-blur-xl">
          {/* Top live strip */}
          <div className="flex items-center justify-between px-5 pt-3.5 pb-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 pulse-dot" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
              </span>
              <span className="rtp-label text-[10px] tracking-[0.25em] uppercase text-emerald-400 font-bold">
                Real-Time Performance
              </span>
            </div>
            <a
              href="#testimonials"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#testimonials')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate-400 hover:text-amber-400 font-semibold"
            >
              View Outcomes <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>

          {/* 5-tile grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            <Tile
              icon={Trophy}
              accent="amber"
              viz={<MiniBars color="#F59E0B" />}
              value={<Tenders />}
              label="Tenders Won"
              sub="Govt · PSU · Private"
            />
            <Tile
              icon={Timer}
              accent="violet"
              viz={<ClockArc size={36} color="#A78BFA" />}
              value={<Hours />}
              label="Bid Turnaround"
              sub="Urgent SLA"
              dividerStart
            />
            <Tile
              icon={BarChart3}
              accent="emerald"
              viz={<ProgressRing value={94} size={36} stroke={3} color="#34D399" />}
              value={<Success />}
              label="Bid Success Rate"
              sub="3.3× industry avg"
              dividerStart
            />
            <Tile
              icon={CheckCircle2}
              accent="blue"
              viz={<OnTimeBar color="#60A5FA" />}
              value={<Portals />}
              label="On-Time Submission"
              sub="Zero missed deadlines"
              dividerStart
            />
            <Tile
              icon={Layers}
              accent="pink"
              viz={<PieWedges color="#F472B6" />}
              value={<Sectors />}
              label="Sectors Served"
              sub="Cross-industry depth"
              dividerStart
            />
          </div>

          {/* Bottom NDA strip */}
          <div className="flex items-center gap-2 px-5 py-2.5 border-t border-white/5">
            <Sparkles className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-slate-500">
              Outcomes verified internally · Client identities under NDA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroStatsRibbon;
